<template>
  <u-app id="app">
    <template v-if="!isLoginPage && sessionReady">
      <u-hide-at breakpoint="md">
        <m-app-statusbar />
      </u-hide-at>

      <u-hide-at breakpoint="md">
        <div>
          <!--          <m-app-header />-->
          <m-app-navigation />
        </div>
      </u-hide-at>

      <u-show-at breakpoint="md">
        <div>
          <app-sidebar />
        </div>
      </u-show-at>
    </template>

    <u-main v-if="!isLoginPage && sessionReady">
      <u-show-at breakpoint="md">
        <app-header />
      </u-show-at>

      <router-view />
    </u-main>

    <router-view v-if="isLoginPage" />

    <div
      v-if="!isLoginPage && !sessionReady"
      class="flex w-full min-h-screen items-center justify-center p-6"
    >
      <core-loading v-if="sessionState === 'checking'" />

      <u-alert
        v-else
        variant="danger"
        icon="mdi-alert-outline"
        class="w-full max-w-xl"
      >
        <template #title>Не удалось проверить сессию</template>

        <p>{{ sessionErrorText }}</p>

        <u-button class="mt-4" variant="secondary" text @click="checkSession">
          Повторить
        </u-button>

        <u-button class="mt-4 ml-2" variant="secondary" text @click="goToLogin">
          Войти
        </u-button>
      </u-alert>
    </div>

    <connection-banner />
  </u-app>
</template>

<script>
import Api from "./app/services/Api";
import { saveSession } from "./app/services/session";
import AppSidebar from "@/app/shared/new-component/AppSidebar";
import ConnectionBanner from "@/app/shared/new-component/ConnectionBanner";
// import MAppHeader from "@/app/shared/new-component/MAppHeader";
// import MAppNavigation from "@/app/shared/new-component/MAppNavigation";
import MAppStatusbar from "@/app/shared/new-component/MAppStatusbar";
import AppHeader from "@/app/shared/new-component/AppHeader";
import MAppNavigation from "@/app/shared/new-component/MAppNavigation";

export default {
  components: {
    MAppNavigation,
    AppHeader,
    MAppStatusbar,
    ConnectionBanner,
    // MAppNavigation,
    // MAppHeader,
    AppSidebar
  },

  data: () => ({
    // checking → сессия проверяется, ready → пользователь подтверждён,
    // failed → подтвердить не удалось (рабочие экраны не показываем).
    sessionState: "checking",
    sessionError: null
  }),

  computed: {
    isLoginPage() {
      return this.$route.name === "login";
    },

    sessionReady() {
      return this.sessionState === "ready";
    },

    /** Явный выход учителя: не даём автоматически вернуть его в кабинет. */
    explicitLogout() {
      return this.$store.state.explicitLogout;
    },

    sessionErrorText() {
      if (!this.sessionError) return "Не удалось подтвердить пользователя.";

      const { kind } = Api.describeError(this.sessionError);

      if (kind === "offline") {
        return "Устройство не в сети. Проверьте подключение и повторите.";
      }

      if (kind === "cloud_down" || kind === "school_down") {
        return "Нет связи с сервисом — сессию проверить нельзя. Повторите попытку позже.";
      }

      if (kind === "session" || kind === "denied") {
        return "Сессия недействительна — войдите заново.";
      }

      return "Не удалось подтвердить пользователя. Повторите попытку.";
    }
  },

  watch: {
    isLoginPage(value) {
      // На форму входа попадают и по ссылке с кодом, и при отказе сессии.
      // Поэтому здесь только сбрасываем состояние проверки: решение «сессия
      // жива → уйти в кабинет» принимает Login.vue после реального запроса.
      // Если уводить отсюда по устаревшему sessionReady, то 401 на данных
      // (сессия истекла) зациклит навигацию вход ↔ кабинет.
      if (value) {
        this.sessionState = "checking";
        this.sessionError = null;
        return;
      }

      // Ушли с формы входа без подтверждённой сессии (например, только что
      // вошли) — проверяем её, иначе рабочие экраны не откроются.
      if (!this.sessionReady) this.checkSession();
    }
  },

  async created() {
    await this.checkSession();
  },

  methods: {
    /**
     * `current-user` — это и есть проверка сессии. Пока она не прошла, рабочие
     * экраны (создание заявки и т.п.) не показываем: иначе неавторизованный
     * пользователь видит интерфейс, который всё равно не сможет использовать.
     */
    async checkSession() {
      this.sessionState = "checking";
      this.sessionError = null;

      try {
        const { data: profile } = await Api.authorization.currentUser();
        saveSession(profile);
        this.sessionState = "ready";
        this.$store.commit("setExplicitLogout", false);

        await this.openHomeRoute();
      } catch (error) {
        this.sessionError = error;
        this.sessionState = "failed";

        const { kind, status } = Api.describeError(error);

        // Явный отказ авторизации: повторять нечего, уводим на вход.
        // little на неавторизованный запрос отвечает 404, big — 401.
        if (kind === "session" || kind === "denied" || status === 404) {
          // Пока идёт переход, держим спиннер, чтобы не мигала карточка ошибки.
          this.sessionState = "checking";
          await this.goToLogin();

          // Перейти на вход не удалось — показываем состояние с кнопками.
          if (!this.isLoginPage) this.sessionState = "failed";
        }
      }
    },

    async goToLogin() {
      if (this.$router.currentRoute.name === "login") return;

      try {
        await this.$router.push({ name: "login" });
      } catch (error) {
        // Навигация может быть прервана — тогда остаёмся на текущем экране.
      }
    },

    /**
     * Подтверждённую сессию показываем в кабинете, а не на форме входа: ссылка
     * оператора ведёт на `#/login?token=...`, и если код уже погашен, учитель
     * оставался на входе, хотя сессия жива.
     */
    async openHomeRoute() {
      if (this.explicitLogout) return;
      if (this.$router.currentRoute.name !== "login") return;

      try {
        await this.$router.replace({ name: "home" });
      } catch (error) {
        // Навигация может быть прервана — тогда остаёмся на текущем экране.
      }
    }
  }
};
</script>
